// Copyright (c) 2017 VMware, Inc. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//    http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import {Component, EventEmitter, Output} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ConfirmationMessage } from './confirmation-message';
import { ConfirmationAcknowledgement } from './confirmation-state-message';
import { ConfirmationState, ConfirmationTargets, ConfirmationButtons } from '../shared/shared.const';

import {BatchInfo} from './confirmation-batch-message';

@Component({
    selector: 'confirmation-dialog',
    template: `<clr-modal [(clrModalOpen)]="opened" [clrModalClosable]="false" [clrModalStaticBackdrop]="true">
    <h3 class="modal-title" class="confirmation-title" style="margin-top: 0px;">{{dialogTitle}}</h3>
    <div class="modal-body">
        <div class="confirmation-icon-inline">
            <clr-icon shape="warning" class="is-warning" size="64"></clr-icon>
        </div>
        <div class="confirmation-content">{{dialogContent}}</div>
    </div>
    <div class="modal-footer" [ngSwitch]="buttons">
        <ng-template [ngSwitchCase]="0">
            <button type="button" class="btn btn-outline" (click)="cancel()">{{'BUTTON.CANCEL' | translate}}</button>
            <button type="button" class="btn btn-primary" (click)="confirm()">{{'BUTTON.CONFIRM' | translate}}</button>
        </ng-template>
        <ng-template [ngSwitchCase]="1">
            <button type="button" class="btn btn-outline" (click)="cancel()">{{'BUTTON.NO' | translate}}</button>
            <button type="button" class="btn btn-primary" (click)="confirm()">{{ 'BUTTON.YES' | translate}}</button>
        </ng-template>
        <ng-template [ngSwitchCase]="2">
            <button type="button" class="btn btn-outline" (click)="cancel()" [hidden]="isDelete">{{'BUTTON.CANCEL' | translate}}</button>
            <button type="button" class="btn btn-danger" (click)="confirm()" [hidden]="isDelete">{{'BUTTON.DELETE' | translate}}</button>
        </ng-template>
        <ng-template [ngSwitchCase]="3">
            <button type="button" class="btn btn-primary" (click)="cancel()">{{'BUTTON.CLOSE' | translate}}</button>
        </ng-template>
        <ng-template [ngSwitchCase]="4">
            <button type="button" class="btn btn-outline" (click)="cancel()" [hidden]="isDelete">{{'BUTTON.CANCEL' | translate}}</button>
            <button type="button" class="btn btn-primary" (click)="confirm()" [hidden]="isDelete">{{'BUTTON.REPLICATE' | translate}}</button>
        </ng-template>
    </div>
</clr-modal>`,
    styles: [`.confirmation-icon-inline{display:inline-block}.confirmation-title{line-height:24px;color:#000;font-size:22px}.confirmation-content{font-size:14px;color:#565656;line-height:24px;display:inline-block;vertical-align:middle;width:80%;white-space:pre-wrap}.batchInfoUl{padding:20px;list-style-type:none}.batchInfoUl li{line-height:24px;border-bottom:1px solid #e8e8e8}.batchInfoUl li span:first-child{padding-right:20px;width:240px;display:inline-block;color:#666;text-overflow:ellipsis;overflow:hidden;vertical-align:middle}.batchInfoUl li span:last-child{width:220px;display:inline-block;color:#666}.batchInfoUl li span i{display:inline-block;line-height:1.2em;font-size:.8em;color:#999}.batchInfoUl li span a{cursor:pointer;text-decoration:underline}`]
})

export class ConfirmationDialogComponent {
    opened = false;
    dialogTitle = '';
    dialogContent = '';
    message: ConfirmationMessage;
    buttons: ConfirmationButtons;

    @Output() confirmAction = new EventEmitter<ConfirmationAcknowledgement>();
    @Output() cancelAction = new EventEmitter<ConfirmationAcknowledgement>();
    isDelete = false;

    constructor(
        private translate: TranslateService) {}

    open(msg: ConfirmationMessage): void {
        this.dialogTitle = msg.title;
        this.message = msg;
        this.translate.get(this.dialogTitle).subscribe((res: string) => this.dialogTitle = res);
        this.translate.get(msg.message, { 'param': msg.param }).subscribe((res: string) => {
            this.dialogContent = res;
        });
        // Open dialog
        this.buttons = msg.buttons;
        this.opened = true;
    }


    colorChange(list: BatchInfo) {
        if (!list.loading && !list.errorState) {
            return 'green';
        }else if (!list.loading && list.errorState) {
            return 'red';
        }else {
            return '#666';
        }
    }

    toggleErrorTitle(errorSpan: any) {
        errorSpan.style.display = (errorSpan.style.display === 'none') ? 'block' : 'none';
    }

    close(): void {
        this.opened = false;
    }

    cancel(): void {
        if (!this.message) {
            // Inproper condition
            this.close();
            return;
        }

        let data: any = this.message.data ? this.message.data : {};
        let target = this.message.targetId ? this.message.targetId : ConfirmationTargets.EMPTY;
        this.cancelAction.emit(new ConfirmationAcknowledgement(
            ConfirmationState.CANCEL,
            data,
            target
        ));
        this.isDelete = false;
        this.close();
    }

    confirm(): void {
        if (!this.message) {// Inproper condition
            this.close();
            return;
        }

        let data: any = this.message.data ? this.message.data : {};
        let target = this.message.targetId ? this.message.targetId : ConfirmationTargets.EMPTY;
        let message = new ConfirmationAcknowledgement(
            ConfirmationState.CONFIRMED,
            data,
            target
        );
        this.confirmAction.emit(message);
        this.close();
    }
}
